import React, { useState, useEffect, useRef, useCallback } from "react";
import "../../App.css";
import { useApp } from '../../core/context';
import { INR, initials, today, uuid, invoiceStatus, dateInRange, logAudit, notify, notifyCompanyUsers, isTaskOverdue, canClientSubmitTask, docDisplayName, getLatestSubmission, parseNotifMeta } from '../../core/helpers';
import { sb } from '../../core/db';
import { TASK_STATUSES, TASK_REQUIREMENT_TYPES } from '../../core/constants';
import { resolveDocTemplate } from '../../core/seed-doc-templates';
import { Badge } from '../../components/Badge';
import { FilterBar } from '../../components/FilterBar';
import { IconProperties } from '../../icons.properties';
import { Icon, InlineIcon } from '../../components/Icon';
import { DocActions } from '../../components/DocActions';
import { uploadClientDoc } from '../../storage';


function ClientTaskSubmitModal({ data, onClose, submissions, setSubmissions, tasks, setTasks, showToast }) {
  const { task } = data;
  const { docTpls, clients, runSubmitting, notifications, setNotifs } = useApp();
  const fileRef    = useRef(null);
  const [pendingUpload, setPendingUpload] = useState(null); // { directorIdx, slotId }
  const [loading,  setLoading]  = useState(false);
  const [uploadingSlot, setUploadingSlot] = useState(null); // #G22 per-slot upload state
  const [note,     setNote]     = useState("");

  const tpl = resolveDocTemplate(task.docTemplateId, docTpls);
  const isDirectorLevel = tpl?.collectionType === "director";

  // Figure out how many directors this task is for
  // task.directorCount is set when auto-created from DSC qty, else default 1
  const directorCount = task.directorCount || 1;
  const directors = Array.from({ length: directorCount }, (_, i) => i + 1);

  // #G23 Most recent prior submission for this task — preload it so a resubmit isn't blank
  const priorSub = getLatestSubmission(submissions, task.id);
  const client = clients.find(c => c.id === task.clientId);
  const canSubmit = canClientSubmitTask(task, priorSub, client);

  // State: { [dirIdx]: { [fieldId]: value } }
  const [formData, setFormData] = useState(() => {
    const d = {};
    directors.forEach(n => { d[n] = priorSub?.formData?.[n] ? { ...priorSub.formData[n] } : {}; });
    return d;
  });

  // State: { [dirIdx]: { [slotId]: { name, size, path, url } } }
  const [docData, setDocData] = useState(() => {
    const d = {};
    directors.forEach(n => { d[n] = priorSub?.documents?.[n] ? { ...priorSub.documents[n] } : {}; });
    return d;
  });

  const noActionNeeded = !tpl || (tpl.infoFields.length === 0 && tpl.docSlots.length === 0);

  // Validation — all required fields and docs per director
  const isComplete = () => {
    if (noActionNeeded) return true;
    const checkDirs = isDirectorLevel ? directors : [1];
    for (const n of checkDirs) {
      for (const f of (tpl?.infoFields || [])) {
        if (f.required && !(formData[n]?.[f.id])) return false;
      }
      for (const s of (tpl?.docSlots || [])) {
        if (s.required && !(docData[n]?.[s.id])) return false;
      }
    }
    return true;
  };

  const triggerUpload = (dirIdx, slotId) => {
    setPendingUpload({ dirIdx, slotId });
    fileRef.current?.click();
  };
  const onFile = async (e) => {
    if (e.target.files[0] && pendingUpload) {
      const f = e.target.files[0];
      const { dirIdx, slotId } = pendingUpload;
      setUploadingSlot(`${dirIdx}_${slotId}`);
      const slotLabel = tpl?.docSlots?.find(s => s.id === slotId)?.label || slotId;
      const displayName = docDisplayName(slotLabel, f.name);
      const safe = displayName.replace(/[^a-zA-Z0-9._-]/g, "_");
      const path = `${task.clientId}/${task.id}/${dirIdx}_${slotId}_${Date.now()}_${safe}`;
      try {
        await uploadClientDoc(path, f);
        setDocData(d => ({ ...d, [dirIdx]: { ...d[dirIdx], [slotId]: { name: displayName, size: (f.size/1024).toFixed(0)+"KB", path } } }));
      } catch (err) { showToast("Upload failed: " + (err.message || "please retry").slice(0, 80), "error"); }
      setUploadingSlot(null);
      e.target.value = "";
    }
  };

  const setField = (dirIdx, fieldId, val) =>
    setFormData(d => ({ ...d, [dirIdx]: { ...d[dirIdx], [fieldId]: val } }));

  // #G25 remove/replace an individual attachment, and highlight items the team flagged for correction
  const removeDoc = (dirIdx, slotId) =>
    setDocData(d => ({ ...d, [dirIdx]: { ...d[dirIdx], [slotId]: null } }));
  const flaggedKeys  = (priorSub?.rejectedItems) || [];
  const isDocFlagged   = (dirIdx, slotId)  => flaggedKeys.includes(`doc:${dirIdx}:${slotId}`);
  const isFieldFlagged = (dirIdx, fieldId) => flaggedKeys.includes(`field:${dirIdx}:${fieldId}`);

  const submit = async () => {
    if (!isComplete()) { showToast("Please complete all required fields and upload all required documents", "error"); return; }

    await runSubmitting('Submitting your response…', async () => {
    const newSub = {
      id: uuid(), taskId: task.id, clientId: task.clientId,
      status: "submitted", locked: true,
      formData, documents: docData,
      submittedAt: new Date().toLocaleDateString("en-IN",{day:"numeric",month:"short",year:"numeric"}),
      createdAt: new Date().toISOString(),
      reviewNote: "", reviewedAt: null, note,
    };

    // Save submission to DB
    await sb.insert("submissions", {
      id:           newSub.id,
      task_id:      task.id,
      company_id:   task.clientId || null,
      status:       "submitted",
      form_data:    formData,
      documents:    docData,
      note:         note || "",
      submitted_at: newSub.submittedAt,
      locked:       true,
    });

    // Update task status to team_approval in DB
    await sb.update("tasks", "id", task.id, {
      status:     "team_approval",
      updated_at: new Date().toISOString(),
    });

    // Update local state
    setSubmissions(ss => [...ss, newSub]);
    setTasks(ts => ts.map(t => t.id === task.id ? { ...t, status: "team_approval" } : t));

    (notifications || []).forEach(n => {
      const meta = parseNotifMeta(n);
      if (n.type === "changes_requested" && meta?.taskId === task.id && !n.read) {
        sb.update("notifications", "id", n.id, { read: true }).catch(() => {});
      }
    });
    setNotifs?.(ns => ns.map(n => {
      const meta = parseNotifMeta(n);
      if (n.type === "changes_requested" && meta?.taskId === task.id) return { ...n, read: true };
      return n;
    }));

    // Notify assigned reviewer
    notify(task.assignedTo, {
      type: "submission",
      title: "New submission to review",
      body: `${task.clientName || "A client"} submitted "${task.title}" for your review.`,
      meta: { taskId: task.id, submissionId: newSub.id, modal: "review-submission" },
    });

    showToast("Submitted successfully! Your team will review within 1–2 working days.", "success");
    onClose();
    }, 'submit');
  };

  // Renders form + docs for one director (or "company" if common)
  const renderSection = (dirIdx, label) => (
    <div key={dirIdx} style={{ border:"1px solid var(--border)", borderRadius:10, overflow:"hidden", marginBottom:16 }}>
      {/* Section header */}
      <div style={{ padding:"11px 16px", background:"linear-gradient(90deg,#F0F6FF,var(--white))", borderBottom:"1px solid var(--border)", display:"flex", alignItems:"center", gap:10 }}>
        <div style={{ width:30,height:30,borderRadius:"50%",background:"var(--blue)",display:"flex",alignItems:"center",justifyContent:"center",fontSize:12,fontWeight:700,color:"#fff",flexShrink:0 }}>
          {isDirectorLevel ? dirIdx : <Icon name="company" size={16} color="#fff" />}
        </div>
        <div>
          <div style={{ fontSize:13,fontWeight:700 }}>{label}</div>
          <div style={{ fontSize:11,color:"var(--muted)" }}>
            {(tpl?.infoFields||[]).filter(f=>f.required).length} required fields
            {" · "}{(tpl?.docSlots||[]).filter(s=>s.required).length} required documents
          </div>
        </div>
      </div>

      {/* Info fields */}
      {(tpl?.infoFields||[]).length > 0 && (
        <div style={{ padding:"14px 16px", borderBottom:(tpl?.docSlots||[]).length>0?"1px solid var(--border)":undefined }}>
          <div style={{ fontSize:11,fontWeight:700,color:"var(--muted)",textTransform:"uppercase",letterSpacing:"1px",marginBottom:10 }}><InlineIcon name="clipboard" size={13} gap={5}>Information</InlineIcon></div>
          <div className="form-grid-2">
            {(tpl?.infoFields||[]).map(f => (
              <div key={f.id} className="f-group" style={{ gridColumn: f.type==="textarea"?"1/-1":undefined }}>
                <label className="f-label">
                  {f.label}
                  {f.required && <span style={{ color:"var(--red)" }}> *</span>}
                  {isFieldFlagged(dirIdx,f.id) && <span style={{ marginLeft:6,fontSize:10,fontWeight:700,color:"#fff",background:"var(--red)",padding:"1px 6px",borderRadius:10 }}>NEEDS CORRECTION</span>}
                </label>
                {f.type === "textarea" ? (
                  <textarea className="f-textarea" rows={2}
                    value={formData[dirIdx]?.[f.id]||""}
                    onChange={e=>setField(dirIdx,f.id,e.target.value)}
                  />
                ) : f.type === "select" ? (
                  <select className="f-select"
                    value={formData[dirIdx]?.[f.id]||""}
                    onChange={e=>setField(dirIdx,f.id,e.target.value)}>
                    <option value="">Select…</option>
                    {(f.options||[]).map(o=><option key={o}>{o}</option>)}
                  </select>
                ) : (
                  <input className="f-input" type={f.type||"text"}
                    value={formData[dirIdx]?.[f.id]||""}
                    onChange={e=>setField(dirIdx,f.id,e.target.value)}
                  />
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Document slots */}
      {(tpl?.docSlots||[]).length > 0 && (
        <div style={{ padding:"14px 16px" }}>
          <div style={{ fontSize:11,fontWeight:700,color:"var(--muted)",textTransform:"uppercase",letterSpacing:"1px",marginBottom:10 }}><InlineIcon name="file" size={13} gap={5}>Documents</InlineIcon></div>
          {(tpl?.docSlots||[]).map(slot => {
            const up = docData[dirIdx]?.[slot.id];
            const flagged = isDocFlagged(dirIdx, slot.id);
            const busy = uploadingSlot === `${dirIdx}_${slot.id}`;
            return (
              <div key={slot.id} style={{ display:"flex",alignItems:"center",gap:12,padding:"10px",borderRadius:8,marginBottom:4,border:flagged?"1px solid #FCA5A5":"1px solid transparent",background:flagged?"#FEF2F2":"transparent",borderBottom:flagged?"1px solid #FCA5A5":"1px solid #F3F4F6" }}>
                <div style={{ width:32,height:32,borderRadius:8,background:up?"#F0FDF4":"#F9FAFB",border:`1px solid ${up?"#BBF7D0":"var(--border)"}`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:14,flexShrink:0 }}>
                  <Icon name={up ? "check" : "doc"} size={16} color={up ? "var(--green)" : undefined} />
                </div>
                <div style={{ flex:1 }}>
                  <div style={{ fontSize:13,fontWeight:500 }}>
                    {slot.label}
                    {slot.required && <span style={{ color:"var(--red)",marginLeft:3 }}>*</span>}
                    {flagged && <span style={{ marginLeft:6,fontSize:10,fontWeight:700,color:"#fff",background:"var(--red)",padding:"1px 6px",borderRadius:10 }}>NEEDS CORRECTION</span>}
                  </div>
                  {up
                    ? <div style={{ fontSize:11,color:"var(--green)",marginTop:1 }}><InlineIcon name="check" size={11} gap={4}>{up.name} · {up.size}</InlineIcon></div>
                    : <div style={{ fontSize:11,color:"var(--muted)" }}>{slot.required?"Required":"Optional"}</div>
                  }
                </div>
                {busy
                  ? <span style={{ fontSize:11,color:"var(--muted)" }}>Uploading…</span>
                  : !up
                    ? <button className="btn btn-ghost btn-sm" onClick={()=>triggerUpload(dirIdx,slot.id)}><InlineIcon name="upload" size={13} gap={5}>Upload</InlineIcon></button>
                    : <div style={{ display:"flex",gap:6 }}>
                        <button className="btn btn-ghost btn-sm" onClick={()=>triggerUpload(dirIdx,slot.id)}><InlineIcon name="repeat" size={13} gap={5}>Replace</InlineIcon></button>
                      </div>
                }
              </div>
            );
          })}
        </div>
      )}
    </div>
  );

  const done = isComplete();

  if (!canSubmit) {
    return (
      <div className="modal-box" style={{ maxWidth: 480 }}>
        <div className="modal-head">
          <div className="modal-title">Upload not available</div>
          <button className="modal-close" onClick={onClose}><Icon name="close" size={16} /></button>
        </div>
        <div className="modal-body" style={{ fontSize: 13, color: "var(--muted)", lineHeight: 1.5 }}>
          {priorSub?.status === "changes_requested"
            ? "Your team is still processing the previous review. Please refresh the page or contact support if this persists."
            : "Document uploads for this task are disabled until your team requests documents or enables uploading for your account."}
        </div>
        <div className="modal-foot">
          <button className="btn btn-primary" onClick={onClose}>Close</button>
        </div>
      </div>
    );
  }

  return (
    <div className="modal-box" style={{ maxWidth:640 }}>
      <div className="modal-head">
        <div>
          <div className="modal-title">{task.title}</div>
          <div style={{ fontSize:12,color:"var(--muted)",marginTop:2 }}>
            {tpl ? `${tpl.name} — ${isDirectorLevel?`${directorCount} director${directorCount>1?"s":""}`:""} · ${tpl.collectionType==="director"?"Director-level collection":"Company-level collection"}` : "No collection required"}
          </div>
        </div>
        <button className="modal-close" onClick={onClose}><Icon name="close" size={16} /></button>
      </div>
      <div className="modal-body">
        <input type="file" ref={fileRef} style={{ display:"none" }} onChange={onFile} />

        {priorSub && (priorSub.reviewNote || priorSub.status==="changes_requested") && (
          <div style={{ padding:"11px 14px",background:"#FEF2F2",border:"1px solid #FCA5A5",borderRadius:8,marginBottom:16,fontSize:13,color:"#991B1B" }}>
            <Icon name="repeat" size={14} /> <strong>Changes requested by your team:</strong> {priorSub.reviewNote || "Please review and correct your submission."}
            <div style={{ fontSize:11,color:"#B45309",marginTop:4 }}>
              Your previous answers and files are pre-filled below — just fix what's needed and resubmit.
              {flaggedKeys.length > 0 && <> Items marked <strong>NEEDS CORRECTION</strong> are the ones your team asked you to redo.</>}
            </div>
          </div>
        )}

        <div style={{ padding:"10px 14px",background:"#FFF7ED",border:"1px solid #FED7AA",borderRadius:8,marginBottom:16,fontSize:12,color:"#C2410C" }}>
          <Icon name="warning" size={14} /> Please complete all required fields and upload all documents before submitting.
          You can <strong>replace</strong> a file before submitting. After you submit, the documents lock — only your team can request changes.
        </div>

        {noActionNeeded ? (
          <div style={{ padding:"16px",background:"#F0FDF4",border:"1px solid #BBF7D0",borderRadius:10,fontSize:13,color:"var(--green)" }}>
            <Icon name="check" size={14} /> No information or documents required for this task. Click Submit to acknowledge.
          </div>
        ) : (
          <>
            {isDirectorLevel
              ? directors.map(n => renderSection(n, `Director ${n}`))
              : renderSection(1, "Company Information")
            }
          </>
        )}

        <div className="f-group" style={{ marginTop:16 }}>
          <label className="f-label">Additional Note for Team (optional)</label>
          <textarea className="f-textarea" rows={2} placeholder="Any message for your team…" value={note} onChange={e=>setNote(e.target.value)} />
        </div>

        {!noActionNeeded && (
          <div style={{ marginTop:12,padding:"10px 14px",background:done?"#F0FDF4":"#FFF1F2",border:`1px solid ${done?"#BBF7D0":"#FECACA"}`,borderRadius:8,fontSize:12,color:done?"var(--green)":"var(--red)",fontWeight:600 }}>
            {done ? <InlineIcon name="check" size={13} gap={5}>All required fields complete — ready to submit!</InlineIcon> : <InlineIcon name="warning" size={13} gap={5}>Some required fields or documents are missing</InlineIcon>}
          </div>
        )}
      </div>
      <div className="modal-foot">
        <button className="btn" onClick={onClose}>Cancel</button>
        <button className="btn btn-primary" onClick={submit} disabled={loading||(!noActionNeeded&&!done)} style={{ opacity:(!noActionNeeded&&!done)?0.5:1 }}>
          {loading
            ? <span style={{ display:"inline-block",width:16,height:16,border:"2px solid rgba(255,255,255,.3)",borderTopColor:"#fff",borderRadius:"50%",animation:"spin .7s linear infinite" }}/>
            : "Submit for Review →"
          }
        </button>
      </div>
    </div>
  );
}

// ─── REVIEW SUBMISSION MODAL (Team) ──────────────────────────────────

function ReviewSubmissionModal({ data, onClose, submissions, setSubmissions, tasks, setTasks, showToast }) {
  const { submissionId } = data;
  const { docTpls, userLinks, users, clients, setClients, runSubmitting, submitBusy } = useApp();
  const sub  = submissions.find(s => s.id === submissionId);
  const task = tasks.find(t => t.id === sub?.taskId);
  const tpl  = resolveDocTemplate(task?.docTemplateId, docTpls);
  const [note, setNote] = useState("");
  const [rejected, setRejected] = useState([]);
  const toggleReject = (key) => setRejected(r => r.includes(key) ? r.filter(k=>k!==key) : [...r, key]);

  if (!sub || !task) return null;

  const decide = (approved) => runSubmitting(approved ? 'Approving…' : 'Sending decline…', async () => {
    if (approved && rejected.length > 0) {
      showToast("Uncheck all flagged items before approving, or use Decline to request changes.", "error");
      return;
    }
    if (!approved && !note && rejected.length === 0) { showToast("Add a note or flag the items that need correction", "error"); return; }
    const newStatus  = approved ? "approved" : "changes_requested";
    const taskStatus = approved ? "completed" : "client_action";
    const flags = approved ? [] : rejected;

    // Save to DB
    await sb.update("submissions", "id", submissionId, {
      status:      newStatus,
      review_note: note,
      rejected_items: flags,
      reviewed_at: new Date().toLocaleDateString("en-IN", {day:"numeric",month:"short",year:"numeric"}),
    });
    await sb.update("tasks", "id", sub.taskId, {
      status:          taskStatus,
      completed_date:  approved ? new Date().toISOString().split("T")[0] : null,
      updated_at:      new Date().toISOString(),
    });
    if (!approved) {
      await sb.update("companies", "id", sub.clientId, { upload_enabled: true });
      setClients(cs => cs.map(c => c.id === sub.clientId ? { ...c, uploadEnabled: true } : c));
    }

    // Update local state
    setSubmissions(ss => ss.map(s => s.id===submissionId ? {
      ...s, status: newStatus, reviewNote: note, rejectedItems: flags,
      reviewedAt: new Date().toLocaleDateString("en-IN",{day:"numeric",month:"short",year:"numeric"}),
    } : s));
    setTasks(ts => ts.map(t => t.id===sub.taskId ? {
      ...t, status: taskStatus,
      completedDate: approved ? new Date().toLocaleDateString("en-IN",{day:"numeric",month:"short",year:"numeric"}) : null,
    } : t));

    // Notify client users linked to this company
    await notifyCompanyUsers(sub.clientId, { userLinks, users, clients }, {
      type: approved ? "approved" : "changes_requested",
      title: approved ? "Documents approved" : "Documents declined — reupload required",
      body: approved
        ? `Your submission for "${task.title}" was approved.`
        : `Changes needed on "${task.title}": ${note || "Please reupload the flagged documents."}`,
      meta: { taskId: task.id, submissionId, modal: "client-submit-task" },
    });
    showToast(approved ? "Approved! Task marked complete." : "Declined — client notified to reupload.", approved ? "success" : "warning");
    onClose();
  }, 'review');

  // Render all submitted documents with download buttons
  const renderDocs = () => {
    const docs = sub.documents || {};
    const allDocs = [];
    Object.entries(docs).forEach(([dirKey, slots]) => {
      if (typeof slots === "object") {
        Object.entries(slots).forEach(([slotId, docData]) => {
          if (docData) {
            const slotName = tpl?.docSlots?.find(s=>s.id===slotId)?.label || slotId;
            allDocs.push({ key:`${dirKey}_${slotId}`, flagKey:`doc:${dirKey}:${slotId}`, name:`${slotName} (Dir ${dirKey})`, data:docData });
          }
        });
      }
    });
    if (allDocs.length === 0) return <div style={{fontSize:12,color:"var(--muted)"}}>No documents attached</div>;
    return allDocs.map(doc => {
      const flagged = rejected.includes(doc.flagKey);
      return (
      <div key={doc.key} style={{display:"flex",alignItems:"center",gap:12,padding:"10px 14px",background:flagged?"#FEF2F2":"var(--cream)",borderRadius:8,marginBottom:8,border:`1px solid ${flagged?"#FCA5A5":"var(--border)"}`}}>
        <Icon name="doc" size={20} />
        <div style={{flex:1,fontSize:13,fontWeight:500}}>{doc.name}</div>
        <label style={{display:"flex",alignItems:"center",gap:5,fontSize:11,color:flagged?"var(--red)":"var(--muted)",cursor:"pointer",fontWeight:600}}>
          <input type="checkbox" checked={flagged} onChange={()=>toggleReject(doc.flagKey)} /> Needs redo
        </label>
        <DocActions doc={doc.data} name={doc.name} onError={(msg) => showToast(msg, "error")} compact />
      </div>
      );
    });
  };

  // Render form data
  const renderFormData = () => {
    const fd = sub.formData || {};
    const rows = [];
    Object.entries(fd).forEach(([dirKey, fields]) => {
      if (typeof fields === "object") {
        Object.entries(fields).forEach(([fieldId, value]) => {
          if (value) {
            const fieldName = tpl?.infoFields?.find(f=>f.id===fieldId)?.label || fieldId;
            rows.push({ key:`${dirKey}_${fieldId}`, flagKey:`field:${dirKey}:${fieldId}`, label:`${fieldName}`, value });
          }
        });
      }
    });
    if (rows.length === 0) return <div style={{fontSize:12,color:"var(--muted)"}}>No form data submitted</div>;
    return rows.map(r => {
      const flagged = rejected.includes(r.flagKey);
      return (
      <div key={r.key} style={{display:"flex",gap:12,padding:"7px 0",borderBottom:"1px solid var(--border)",alignItems:"center",background:flagged?"#FEF2F2":"transparent"}}>
        <div style={{fontSize:12,color:"var(--muted)",width:160,flexShrink:0}}>{r.label}</div>
        <div style={{fontSize:13,fontWeight:500,flex:1}}>{r.value}</div>
        <label style={{display:"flex",alignItems:"center",gap:5,fontSize:11,color:flagged?"var(--red)":"var(--muted)",cursor:"pointer",fontWeight:600}}>
          <input type="checkbox" checked={flagged} onChange={()=>toggleReject(r.flagKey)} /> Needs redo
        </label>
      </div>
      );
    });
  };

  return (
    <div className="modal-box" style={{ maxWidth:680 }}>
      <div className="modal-head">
        <div>
          <div className="modal-title">Review Submission</div>
          <div style={{ fontSize:12,color:"var(--muted)",marginTop:2 }}>{task.title} · {task.clientName} · Submitted {sub.submittedAt}</div>
        </div>
        <button className="modal-close" onClick={onClose}><Icon name="close" size={16} /></button>
      </div>
      <div className="modal-body">
        {/* Documents */}
        <div style={{marginBottom:20}}>
          <div style={{fontSize:13,fontWeight:700,color:"var(--navy)",marginBottom:10}}><InlineIcon name="file" size={14} gap={5}>Submitted Documents</InlineIcon></div>
          {renderDocs()}
        </div>
        {/* Form Data */}
        <div style={{marginBottom:20}}>
          <div style={{fontSize:13,fontWeight:700,color:"var(--navy)",marginBottom:10}}><InlineIcon name="clipboard" size={14} gap={5}>Submitted Information</InlineIcon></div>
          {renderFormData()}
        </div>
        {sub.note && (
          <div style={{padding:"10px 14px",background:"#F0F9FF",borderRadius:8,fontSize:12,marginBottom:16}}>
            <strong>Client Note:</strong> {sub.note}
          </div>
        )}
        <div className="f-group">
          <label className="f-label">Review Note {!true && <span style={{color:"var(--red)"}}>*</span>} (required when requesting changes)</label>
          <textarea className="f-textarea" rows={3}
            placeholder="Approving: optional feedback. Requesting changes: explain exactly what needs to be corrected."
            value={note} onChange={e=>setNote(e.target.value)}
          />
        </div>
      </div>
      <div className="modal-foot">
        <button className="btn" onClick={onClose} disabled={submitBusy}>Cancel</button>
        <button className="btn btn-red" disabled={submitBusy} onClick={() => decide(false)}><InlineIcon name="close" size={13} gap={5}>Decline</InlineIcon></button>
        <button className="btn btn-green" disabled={submitBusy} onClick={() => decide(true)}><InlineIcon name="check" size={13} gap={5}>Approve</InlineIcon></button>
      </div>
    </div>
  );
}

// ─── CLIENT SELF REGISTRATION ─────────────────────────────────────────



export { ClientTaskSubmitModal, ReviewSubmissionModal };
